import React, { useEffect, useState, useMemo } from 'react';
import PublicLayout from '../layouts/PublicLayout';
import { Box, Typography, Card, CardContent, Avatar, Grid, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../services/api';

export default function VipsPage() {
  const theme = useTheme();
  const [vips, setVips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/api/cms')
      .then(res => {
        const items = (res.data || []).filter(i => /^vip$/i.test(i.type) || i.type === 'VIP' );
        const all = items.flatMap(i => Array.isArray(i.contentJSON?.items) ? i.contentJSON.items : []);
        setVips(all);
      })
      .catch(() => setError('Failed to load VIPs'))
      .finally(() => setLoading(false));
  }, []);

  const decoImages = useMemo(() => {
    const base = [
      
      'https://i.postimg.cc/KRHvGpxg/elephant.png',
      'https://i.postimg.cc/svNDjHsS/gazelle.png',
      'https://i.postimg.cc/2VK8k9rZ/girafe.png',
      'https://i.postimg.cc/mt52LpBS/lion.png',
      'https://i.postimg.cc/3dcJ8fY0/zebra.png',
      'https://i.postimg.cc/14WyB7nZ/mount-kilimanjaro.png',
      'https://i.postimg.cc/9wDKJpKV/monkey.png',
      'https://i.postimg.cc/qgKGXSZ6/eagle.png',
      // full set (including Copilot + drum) will be shuffled so all images mix
      'https://i.postimg.cc/PLTwMSJb/Copilot-20260117-135237.png',
      'https://i.postimg.cc/McqQY9H1/drum.png',
    ];
    // Fisher-Yates shuffle to produce a mixed, non-repeating order
    for (let k = base.length - 1; k > 0; k--) {
      const j = Math.floor(Math.random() * (k + 1));
      [base[k], base[j]] = [base[j], base[k]];
    }
    return base;
  }, []);

  const main = vips.filter(v => v.subtype === 'main');
  const normal = vips.filter(v => v.subtype === 'normal' || v.subtype === 'vip');
  const speakers = vips.filter(v => v.subtype === 'speaker');

  return (
    <PublicLayout>
      <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 6, mb: 8, px: 2 }}>
        <Typography variant="h3" fontWeight={800} gutterBottom color="primary">VIPs & Speakers</Typography>

        {loading ? <Typography>Loading...</Typography> : error ? <Typography color="error">{error}</Typography> : (
          <>
            {/* Main VIPs - show 1/2/3 per row depending on screen */}
            {main.length > 0 && (
              <>
                <Typography variant="h5" sx={{ mb: 2, mt: 1, fontWeight: 800 }}>Featured VIPs</Typography>
                <Grid container spacing={4} sx={{ mb: 6 }}>
                  {main.map((m, i) => {
                    const gridProps = main.length === 1 ? { xs: 12, md: 12 } : main.length === 2 ? { xs: 12, md: 6 } : { xs: 12, sm: 6, md: 4 };
                    const imgHeight = main.length === 1 ? { xs: 300, md: 600 } : main.length === 2 ? { xs: 300, md: 520 } : { xs: 280, md: 420 };
                    return (
                      <Grid item key={i} {...gridProps}>
                        <Card sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', boxShadow: 8, height: '100%', transition: 'transform 220ms ease, box-shadow 220ms ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: 12 } }}>
                          <Box sx={{ width: '100%', height: imgHeight, overflow: 'hidden' }}>
                            {m.image ? (
                              <img src={m.image} alt={m.title || m.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.300' }} />}
                            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.12) 10%, rgba(0,0,0,0.64) 90%)' }} />
                            <Chip label="FEATURED" color="secondary" size="small" sx={{ position: 'absolute', top: 12, left: 12, fontWeight: 800 }} />
                          </Box>

                          {/* decorative reflection - alternate side */}
                          <Box sx={{ position: 'absolute', top: 12, right: i % 2 === 0 ? 12 : 'auto', left: i % 2 !== 0 ? 12 : 'auto', width: { xs: 96, sm: 140 }, height: { xs: 96, sm: 160 }, opacity: 0.36, pointerEvents: 'none', zIndex: 3, filter: 'saturate(1.08) contrast(1.12) drop-shadow(0 10px 26px rgba(0,0,0,0.32))', backgroundImage: `url(${decoImages[i % decoImages.length]})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: 'contain', transition: 'opacity 220ms, transform 220ms' }} />

                          <CardContent sx={{ position: 'absolute', bottom: 16, left: 20, right: 20, color: '#fff', py: 0, zIndex: 2 }}>
                            {/* larger decorative image inside the detail area (subtle, non-interactive) */}
                            <Box sx={{ position: 'absolute', top: -28, right: -12, width: { xs: 140, sm: 260 }, height: { xs: 140, sm: 260 }, opacity: 0.18, pointerEvents: 'none', zIndex: 0, filter: 'saturate(1.02) contrast(1.05) drop-shadow(0 10px 26px rgba(0,0,0,0.28))', backgroundImage: `url(${decoImages[i % decoImages.length]})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: 'contain', transition: 'opacity 220ms, transform 220ms' }} />
                            <Typography variant="h6" sx={{ position: 'relative', zIndex: 2, fontWeight: 900, textShadow: '0 6px 18px rgba(0,0,0,0.6)', color: 'common.white' }}>{m.title || m.name}</Typography>
                            {m.quote && (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5, position: 'relative', zIndex: 2 }}>
                                <Typography sx={{ fontSize: 36, lineHeight: 0.8, opacity: 0.9, mr: 0.5 }} color="inherit">“</Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.98, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.92)' }}>{m.quote}</Typography>
                              </Box>
                            )}
                            {m.description && <Typography variant="body2" sx={{ mt: 1, maxWidth: '100%', color: 'rgba(255,255,255,0.96)', position: 'relative', zIndex: 2 }}>{m.description}</Typography>}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}

            {/* VIP Guests */}
            {normal.length > 0 && (
              <>
                <Typography variant="h5" sx={{ mb: 2, mt: 1, fontWeight: 700 }}>VIP Guests</Typography>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {normal.map((n, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <Card sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        alignItems: 'stretch',
                        height: '100%',
                        boxShadow: 3,
                        borderRadius: 2,
                        p: 0,
                        overflow: 'hidden',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        {/* subtle decorative background (behind content) */}
                        <Box sx={{ position: 'absolute', top: 8, left: i % 2 === 0 ? 8 : 'auto', right: i % 2 !== 0 ? 8 : 'auto', width: { xs: 64, sm: 96 }, height: { xs: 64, sm: 96 }, opacity: 0.14, pointerEvents: 'none', zIndex: 0, filter: 'saturate(1.02) contrast(1.04)', backgroundImage: `url(${decoImages[i % decoImages.length]})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: 'contain', transition: 'opacity 220ms, transform 220ms' }} />

                        {n.image && (
                          <Box sx={{
                            flex: { xs: '0 0 auto', sm: '0 0 160px' },
                            width: { xs: '100%', sm: 160 },
                            height: { xs: 180, sm: 'auto' },
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'transparent'
                          }}>
                            <img src={n.image} alt={n.title || n.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          </Box>
                        )}

                        <CardContent sx={{ flex: 1, p: 2, zIndex: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>{n.title || n.name}</Typography>
                          {n.quote && <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.95)' : 'text.secondary', mt: 0.5 }}>{`“${n.quote}”`}</Typography>}
                          {n.description && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{n.description}</Typography>}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {/* Speakers */}
            {speakers.length > 0 && (
              <>
                <Typography variant="h5" sx={{ mb: 2, mt: 1, fontWeight: 700 }}>Speakers</Typography>
                <Grid container spacing={3}>
                  {speakers.map((s, i) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
                      <Card sx={{ position: 'relative', textAlign: 'center', p: 2, boxShadow: 3 }}>
                        <Box sx={{ position: 'absolute', top: 8, right: i % 2 === 0 ? 8 : 'auto', left: i % 2 !== 0 ? 8 : 'auto', width: { xs: 56, sm: 76 }, height: { xs: 56, sm: 84 }, opacity: 0.32, pointerEvents: 'none', zIndex: 2, backgroundImage: `url(${decoImages[i % decoImages.length]})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: 'contain', filter: 'saturate(1.03) contrast(1.05) drop-shadow(0 8px 16px rgba(0,0,0,0.22))' }} />
                        <Avatar src={s.image} alt={s.name} sx={{ width: { xs: 80, sm: 112 }, height: { xs: 80, sm: 112 }, mx: 'auto', mb: 1.5, boxShadow: 4 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>{s.title || s.name}</Typography>
                        {s.quote && <Typography variant="caption" sx={{ display: 'block', color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.88)' : 'text.secondary', mt: 0.5 }}>{s.quote}</Typography>}
                        {s.description && <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>{s.description}</Typography>}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {vips.length === 0 && <Typography color="text.secondary">No VIPs available at this time.</Typography>}
          </>
        )}
      </Box>
    </PublicLayout>
  );
}